import React, { useContext, useEffect, useState } from "react";
import Modal from "./Modal";
import AgeVerificationModal from "./AgeVerificationModal";
import ConsentModal from "./ConsentModal";
import {
  Card,
  CardContent,
  Link,
  TextField,
  Typography,
  Alert,
} from "@mui/material";
import "./styles.css";
import { AppContext } from "../../../contexts/App.Context";
import { COLORS } from "../../../utils/colors";
import { TABS } from "../constants";
import { getItemFromLocalStorage, isValidEmail } from "../../../utils/utility";
import { WEB_APP_LOGIN_KEY } from "../../../constants";

export default (function ({
  modalVisible,
  event,
  handleContinue,
  initialUsername,
  page,
}) {
  const appContext = useContext(AppContext);
  const getDefaultValue = (key) => {
    const localStorageSavedObject = getItemFromLocalStorage(WEB_APP_LOGIN_KEY);
    if (localStorageSavedObject && localStorageSavedObject[key]) {
      return localStorageSavedObject[key];
    } else {
      return "";
    }
  };
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState(getDefaultValue("email"));
  const [age, setAge] = useState(getDefaultValue("age"));
  const [showAgeVerification, setShowAgeVerification] = useState(false);
  const [ageVerificationConfirmed, setAgeVerificationConfirmed] =
    useState(false);

  // Consent modal states
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [currentConsentType, setCurrentConsentType] = useState("");
  const [currentConsentText, setCurrentConsentText] = useState("");
  const [consentQueue, setConsentQueue] = useState([]);
  const [completedConsents, setCompletedConsents] = useState(new Set());
  const [consentResponses, setConsentResponses] = useState({});
  useEffect(() => {
    setUsername(initialUsername);
  }, [initialUsername]);

  // Effect to process consent queue when it's updated
  useEffect(() => {
    if (consentQueue.length > 0 && !showConsentModal) {
      console.log('Consent queue updated, processing next consent');
      const nextConsent = consentQueue[0];
      console.log('Processing next consent:', nextConsent);
      setCurrentConsentType(nextConsent.type);
      setCurrentConsentText(nextConsent.text);
      setShowConsentModal(true);
    }
  }, [consentQueue, showConsentModal]);

  const showEmailField = () => {
    if (!page) {
      return false;
    }
    if (page.includes("social") && event?.loginSettings?.requireEmailSocial) {
      return true;
    } else if (page === TABS.GAMES && event?.loginSettings?.requireEmailGames) {
      return true;
    } else if (
      page === TABS.LIVE &&
      event?.loginSettings?.requireEmailLiveCam
    ) {
      return true;
    } else if (
      page === TABS.LIGHT_SHOW &&
      event?.loginSettings?.requireEmailLightShow
    ) {
      return true;
    } else {
      return false;
    }
  };
  const showAgeField = () => {
    if (!page) {
      return false;
    }
    if (page.includes("social") && event?.loginSettings?.requireAgeSocial) {
      return true;
    } else if (page === TABS.GAMES && event?.loginSettings?.requireAgeGames) {
      return true;
    } else if (page === TABS.LIVE && event?.loginSettings?.requireAgeLiveCam) {
      return true;
    } else if (
      page === TABS.LIGHT_SHOW &&
      event?.loginSettings?.requireAgeLightShow
    ) {
      return true;
    } else {
      return false;
    }
  };

  const getAgeRestriction = () => {
    if (!page) {
      return null;
    }

    let mustBe18 = false;
    let mustBe21 = false;

    if (page.includes("social")) {
      mustBe18 = event?.loginSettings?.mustBe18AndOlderSocial;
      mustBe21 = event?.loginSettings?.mustBe21AndOlderSocial;
    } else if (page === TABS.GAMES) {
      mustBe18 = event?.loginSettings?.mustBe18AndOlderGames;
      mustBe21 = event?.loginSettings?.mustBe21AndOlderGames;
    } else if (page === TABS.LIVE) {
      mustBe18 = event?.loginSettings?.mustBe18AndOlderLiveCam;
      mustBe21 = event?.loginSettings?.mustBe21AndOlderLiveCam;
    } else if (page === TABS.LIGHT_SHOW) {
      mustBe18 = event?.loginSettings?.mustBe18AndOlderLightShow;
      mustBe21 = event?.loginSettings?.mustBe21AndOlderLightShow;
    }

    // 21+ restriction takes precedence over 18+
    if (mustBe21) {
      return "Must be 21 and older";
    } else if (mustBe18) {
      return "Must be 18 and older";
    }

    return null;
  };

  const shouldShowAgeRestrictionAlert = () => {
    const ageRestriction = getAgeRestriction();
    if (!ageRestriction || !age.trim()) {
      return false;
    }

    const userAge = parseInt(age.trim());
    if (isNaN(userAge)) {
      return false;
    }

    if (ageRestriction.includes("21") && userAge < 21) {
      return true;
    } else if (ageRestriction.includes("18") && userAge < 18) {
      return true;
    }

    return false;
  };

  const meetsAgeRequirement = () => {
    const ageRestriction = getAgeRestriction();
    if (!ageRestriction || !age.trim()) {
      return true; // No restriction or no age entered
    }

    const userAge = parseInt(age.trim());
    if (isNaN(userAge)) {
      return true; // Invalid age, let validation handle it
    }

    if (ageRestriction.includes("21")) {
      return userAge >= 21;
    } else if (ageRestriction.includes("18")) {
      return userAge >= 18;
    }

    return true;
  };

  // Helper functions for consent management
  const getConsentConfiguration = () => {
    const consents = [];
    const suffix = getSuffixForPage();
    
    console.log('Getting consent configuration for page:', page);
    console.log('Suffix:', suffix);
    console.log('Event loginSettings:', event?.loginSettings);

    // Check for name consent
    if (showNameField() && event?.loginSettings?.[`nameConsentEnabled${suffix}`]) {
      consents.push({
        type: "Name",
        text: event.loginSettings[`nameConsentText${suffix}`] || "",
      });
      console.log('Added name consent');
    }

    // Check for email consent
    if (showEmailField() && event?.loginSettings?.[`emailConsentEnabled${suffix}`]) {
      consents.push({
        type: "Email",
        text: event.loginSettings[`emailConsentText${suffix}`] || "",
      });
      console.log('Added email consent');
    }

    // Check for age consent
    if (showAgeField() && event?.loginSettings?.[`ageConsentEnabled${suffix}`]) {
      consents.push({
        type: "Age",
        text: event.loginSettings[`ageConsentText${suffix}`] || "",
      });
      console.log('Added age consent');
    }

    // Check for terms consent
    if (event?.loginSettings?.enableTermsOfUse && event?.loginSettings?.termsConsentEnabled) {
      consents.push({
        type: "Terms of Use",
        text: event.loginSettings.termsConsentText || "",
      });
      console.log('Added terms consent');
    }

    console.log('Final consents array:', consents);
    return consents;
  };

  const getSuffixForPage = () => {
    if (!page) return "";
    if (page.includes("social")) return "Social";
    if (page === TABS.GAMES) return "Games";
    if (page === TABS.LIVE) return "LiveCam";
    if (page === TABS.LIGHT_SHOW) return "LightShow";
    return "";
  };

  const showNameField = () => {
    // Name is always required for now
    return true;
  };

  const handleConsentConfirm = () => {
    setShowConsentModal(false);
    
    // Record the consent response
    const updatedConsentResponses = {
      ...consentResponses,
      [currentConsentType]: "Agreed"
    };
    setConsentResponses(updatedConsentResponses);
    console.log('Updated consent responses:', updatedConsentResponses);
    
    // Add to completed consents for tracking
    setCompletedConsents(prev => {
      const newCompleted = new Set([...prev, currentConsentType]);
      console.log('Completed consents:', Array.from(newCompleted));
      return newCompleted;
    });
    
    // Remove from queue - useEffect will process the next one
    setConsentQueue(prev => {
      const newQueue = prev.slice(1);
      console.log('Consent confirmed, remaining queue:', newQueue);
      
      // If no more consents, proceed with submission
      if (newQueue.length === 0) {
        console.log('All consents completed, proceeding with submission');
        console.log('Final completed consents:', Array.from(completedConsents));
        console.log('Consent responses:', updatedConsentResponses);
        setTimeout(() => {
          handleContinue(
            username,
            email,
            age,
            ageVerificationConfirmed ? "Agreed" : false,
            updatedConsentResponses // Pass updated consent responses
          );
        }, 100);
      }
      
      return newQueue;
    });
  };

  const handleConsentCancel = () => {
    setShowConsentModal(false);
    // Clear the queue to stop the consent flow
    setConsentQueue([]);
    setCompletedConsents(new Set());
    setConsentResponses({});
    appContext.triggerToast(true, {
      type: "info",
      message: "Consent cancelled.",
    });
  };

  const onSubmit = () => {
    if (username.trim() === "") {
      appContext.triggerToast(true, {
        type: "warning",
        message: "Please enter your name.",
      });
      return;
    }
    if (showEmailField()) {
      if (!isValidEmail(email.trim())) {
        appContext.triggerToast(true, {
          type: "warning",
          message: "Please enter your valid email id.",
        });
        return;
      }
    }
    if (showAgeField()) {
      if (age.trim() === "") {
        appContext.triggerToast(true, {
          type: "warning",
          message: "Please enter your age.",
        });
        return;
      }

      // Check age restrictions
      const ageRestriction = getAgeRestriction();
      if (ageRestriction) {
        const userAge = parseInt(age.trim());
        if (ageRestriction.includes("21") && userAge < 21) {
          appContext.triggerToast(true, {
            type: "warning",
            message: "You must be 21 or older to participate.",
          });
          return;
        } else if (ageRestriction.includes("18") && userAge < 18) {
          appContext.triggerToast(true, {
            type: "warning",
            message: "You must be 18 or older to participate.",
          });
          return;
        }

        // If user meets age requirement, show age verification modal (unless already confirmed)
        if (meetsAgeRequirement()) {
          if (!ageVerificationConfirmed) {
            setShowAgeVerification(true);
            return;
          }
        }
      }
    }

    // After all validations pass, check for consent requirements
    const requiredConsents = getConsentConfiguration();
    console.log('Required consents:', requiredConsents);
    if (requiredConsents.length > 0) {
      // Initialize consent queue - useEffect will handle processing
      setConsentQueue(requiredConsents);
      setCompletedConsents(new Set());
      return;
    }

    // No consents required, proceed directly
    console.log('No consents required, proceeding directly');
    handleContinue(
      username,
      email,
      age,
      ageVerificationConfirmed ? "Agreed" : false,
      {} // No consents
    );
  };

  const handleAgeVerificationConfirm = () => {
    setShowAgeVerification(false);
    setAgeVerificationConfirmed(true);
    
    // Add debugging
    console.log('Age verification confirmed, checking for consents...');
    
    // After age verification, check for consent requirements
    const requiredConsents = getConsentConfiguration();
    console.log('After age verification - Required consents:', requiredConsents);
    
    if (requiredConsents.length > 0) {
      console.log('Starting consent queue with', requiredConsents.length, 'consents');
      // Initialize consent queue - useEffect will handle processing
      setConsentQueue(requiredConsents);
      setCompletedConsents(new Set());
      return;
    }

    // No consents required, proceed directly
    console.log('No consents required after age verification, proceeding directly');
    handleContinue(
      username,
      email,
      age,
      "Agreed",
      {} // No consents
    );
  };

  const handleAgeVerificationCancel = () => {
    setShowAgeVerification(false);
    // Optionally clear the age field or show a message
    appContext.triggerToast(true, {
      type: "info",
      message: "Age verification cancelled.",
    });
  };

  return (
    <>
      <Modal open={modalVisible} event={event} handleContinue={onSubmit}>
        <div className="gupParentDiv">
          <Card className="gupRoot">
            <CardContent className="adsCardContent">
              <Typography sx={{ my: 1, width: "100%" }}>
                Full Name
                <span
                  style={{
                    color: COLORS.error,
                  }}
                >
                  {" *"}
                </span>
              </Typography>
              <TextField
                size="small"
                variant="filled"
                hiddenLabel
                inputProps={{
                  maxLength: 20,
                }}
                fullWidth
                defaultValue={initialUsername}
                autoFocus
                id="guestusername"
                InputLabelProps={{ style: { color: COLORS.textHeader } }}
                onChange={(e) => {
                  setUsername(e.target.value);
                }}
              />

              {showEmailField() && (
                <>
                  <Typography sx={{ my: 1, width: "100%" }}>
                    Email
                    <span
                      style={{
                        color: COLORS.error,
                      }}
                    >
                      {" *"}
                    </span>
                  </Typography>
                  <TextField
                    type="email"
                    size="small"
                    variant="filled"
                    hiddenLabel
                    inputProps={{
                      maxLength: 40,
                    }}
                    fullWidth
                    defaultValue={getDefaultValue("email")}
                    autoFocus
                    id="guestuseremail"
                    InputLabelProps={{ style: { color: COLORS.textHeader } }}
                    onChange={(e) => {
                      setEmail(e.target.value);
                    }}
                  />
                </>
              )}
              {showAgeField() && (
                <>
                  <Typography sx={{ my: 1, width: "100%" }}>
                    Age
                    <span
                      style={{
                        color: COLORS.error,
                      }}
                    >
                      {" *"}
                    </span>
                  </Typography>
                  <TextField
                    type="number"
                    size="small"
                    variant="filled"
                    hiddenLabel
                    inputProps={{
                      maxLength: 3,
                    }}
                    fullWidth
                    defaultValue={getDefaultValue("age")}
                    autoFocus
                    id="guestuserage"
                    InputLabelProps={{ style: { color: COLORS.textHeader } }}
                    onChange={(e) => {
                      if (e.target.value.length > 3) {
                        setAge(e.target.value.substring(0, 2));
                      } else {
                        setAge(e.target.value);
                      }
                      // Reset age verification when age changes
                      setAgeVerificationConfirmed(false);
                    }}
                  />
                  {shouldShowAgeRestrictionAlert() && (
                    <Alert
                      severity="warning"
                      sx={{
                        mt: 2,
                        backgroundColor: "#fff3cd",
                        border: "1px solid #ffeaa7",
                        "& .MuiAlert-message": {
                          color: "#856404",
                        },
                        py: 0,
                        px: 1,
                        width: "100%",
                      }}
                    >
                      {getAgeRestriction()}
                    </Alert>
                  )}
                  {ageVerificationConfirmed && (
                    <Alert
                      icon={false}
                      severity="success"
                      sx={{
                        mt: 2,
                        backgroundColor: "#d4edda",
                        border: "1px solid #c3e6cb",
                        "& .MuiAlert-message": {
                          color: "#155724",
                        },
                        "& .MuiAlert-icon": {
                          color: "#155724",
                        },
                        py: 0,
                        px: 1,
                        width: "100%",
                        fontSize: 14,
                      }}
                    >
                      ✓ Age verification confirmed
                    </Alert>
                  )}
                </>
              )}
              <Typography
                className="privacyInfo"
                style={{ opacity: 0.7, fontSize: 12, marginTop: "24px" }}
              >
                {"By continuing you agree to\n"}
                {event?.loginSettings?.enableTermsOfUse &&
                event?.loginSettings?.termsOfUseURL &&
                event?.loginSettings?.termsDescription ? (
                  <>
                    <Link
                      style={{ color: "blue" }}
                      href={event?.loginSettings?.termsOfUseURL}
                      target="_blank"
                      underline="hover"
                    >
                      {event?.loginSettings?.termsDescription}
                    </Link>
                    &nbsp;and&nbsp;
                  </>
                ) : (
                  ""
                )}
                {"Uvenu LLC’s "}
                <Link
                  style={{ color: "blue" }}
                  href={"https://www.uvenu.com/uvenu-terms-and-conditions"}
                  target="_blank"
                  underline="hover"
                >
                  Privacy Policy
                </Link>
                {" and "}
                <Link
                  style={{ color: "blue" }}
                  href={"https://www.uvenu.com/uvenu-terms-and-conditions"}
                  target="_blank"
                  underline="hover"
                >
                  Terms
                </Link>
                {"."}
              </Typography>
            </CardContent>
          </Card>
        </div>
      </Modal>

      <AgeVerificationModal
        event={event}
        open={showAgeVerification}
        onConfirm={handleAgeVerificationConfirm}
        onCancel={handleAgeVerificationCancel}
        ageRestriction={getAgeRestriction() || ""}
      />

      <ConsentModal
        event={event}
        open={showConsentModal}
        onConfirm={handleConsentConfirm}
        onCancel={handleConsentCancel}
        consentType={currentConsentType}
        consentText={currentConsentText}
      />
    </>
  );
});
